// API layer for the AI-assisted risk assessment flow (plan §3/§10).
//
// Endpoint paths, payloads, error semantics and timeout budgets are the
// binding contract documented in
// `.claude/docs/ai_assisted_risk_assessment/api_contracts.md` — keep both in
// sync. All calls go through the authenticated axios instance from
// `useClient()` (Bearer token + X-Certain-Client header injected there).

import { AxiosError, AxiosInstance } from "axios";

import {
  AIAcceptRequest,
  AIAcceptResponse,
  AIAnalyzeRequest,
  AIGenerateRequest,
  AIHazardPatch,
  AIHazardShell,
  AIRiskSession,
} from "./types";
import { getMockAIRiskApi } from "./mock/mockApi";

// Client-side timeout budgets (api_contracts.md "Timeout budgets"). The global
// axios client has NO timeout (axios default 0 = unlimited), so the long
// synchronous AI calls set per-request overrides.
export const ANALYZE_TIMEOUT_MS = 300_000;
export const GENERATE_TIMEOUT_MS = 240_000;
export const ACCEPT_TIMEOUT_MS = 60_000;

export interface AIRiskApi {
  // GET / — 404 `no_session` is mapped to `null` (start view); since no error
  // is thrown, the global no-retry-on-404 react-query rule is moot here.
  getSession(projectId: string): Promise<AIRiskSession | null>;
  // POST /analyze — stage 1, synchronous (timeout 300 s).
  analyze(projectId: string, request: AIAnalyzeRequest): Promise<AIRiskSession>;
  // PATCH /hazards/:hazardId — shell edits, dismiss/restore, draft saves,
  // diff merges, proposal discard.
  updateHazard(
    projectId: string,
    hazardId: string,
    patch: AIHazardPatch
  ): Promise<AIHazardShell>;
  // POST /hazards/:hazardId/generate — stage 2, synchronous (timeout 240 s).
  generateHazardDetail(
    projectId: string,
    hazardId: string,
    request: AIGenerateRequest
  ): Promise<AIHazardShell>;
  // POST /hazards/:hazardId/accept — single atomic transaction (timeout 60 s).
  acceptHazard(
    projectId: string,
    hazardId: string,
    request: AIAcceptRequest
  ): Promise<AIAcceptResponse>;
  // DELETE / — discards the session; accepted risks stay.
  discardSession(projectId: string): Promise<void>;
}

const basePath = (projectId: string) =>
  `/projects/${projectId}/ai-risk-assessment`;

// Mock switch (plan §10): evaluated at runtime in dev, but CRA inlines
// REACT_APP_* at build time, so production builds (variable undefined) turn
// the check into a constant `false` and dead-code-eliminate the mock.
export const isAIRiskMockMode = (): boolean =>
  process.env.REACT_APP_AI_RISK_MOCK === "true";

// Client-side timeout (axios ECONNABORTED) and gateway 504 are surfaced via
// the dedicated `risks.ai.errors.timeout` i18n key; manual retry only.
export const isTimeoutError = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;

  return (
    axiosError?.code === "ECONNABORTED" ||
    axiosError?.response?.status === 504
  );
};

const buildRealAIRiskApi = (client: AxiosInstance): AIRiskApi => ({
  getSession: async (projectId) => {
    try {
      const { data } = await client.get<AIRiskSession>(basePath(projectId));

      return data;
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        // 404 `no_session` → no session yet → start view.
        return null;
      }

      throw error;
    }
  },

  analyze: async (projectId, request) => {
    const { data } = await client.post<AIRiskSession>(
      `${basePath(projectId)}/analyze`,
      request,
      { timeout: ANALYZE_TIMEOUT_MS }
    );

    return data;
  },

  updateHazard: async (projectId, hazardId, patch) => {
    const { data } = await client.patch<AIHazardShell>(
      `${basePath(projectId)}/hazards/${hazardId}`,
      patch
    );

    return data;
  },

  generateHazardDetail: async (projectId, hazardId, request) => {
    const { data } = await client.post<AIHazardShell>(
      `${basePath(projectId)}/hazards/${hazardId}/generate`,
      request,
      { timeout: GENERATE_TIMEOUT_MS }
    );

    return data;
  },

  acceptHazard: async (projectId, hazardId, request) => {
    const { data } = await client.post<AIAcceptResponse>(
      `${basePath(projectId)}/hazards/${hazardId}/accept`,
      request,
      { timeout: ACCEPT_TIMEOUT_MS }
    );

    return data;
  },

  discardSession: async (projectId) => {
    await client.delete(basePath(projectId));
  },
});

export const getAIRiskApi = (client: AxiosInstance): AIRiskApi => {
  if (process.env.REACT_APP_AI_RISK_MOCK === "true") {
    return getMockAIRiskApi();
  }

  return buildRealAIRiskApi(client);
};
